import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  EyeOutlined,
  FormOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { FC, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./index.css";

const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
const MAX_CONTENT_LENGTH = 8 * 1024 * 1024;
const SUPPORTED_PASTED_IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);

export interface HiddenMarkdownImage {
  fullMarkdown: string;
  token: string;
}

export interface CollapsedMarkdownImages {
  markdown: string;
  images: HiddenMarkdownImage[];
}

export function collapseMarkdownDataImages(
  markdown: string
): CollapsedMarkdownImages {
  const images: HiddenMarkdownImage[] = [];
  const collapsed = String(markdown || "").replace(
    /!\[([^\]\n]*)\]\((data:image\/(?:png|jpe?g|gif|webp);base64,[^)]+)\)/gi,
    (fullMarkdown, alt: string) => {
      const token = `![${alt}]`;
      images.push({ fullMarkdown, token });
      return token;
    }
  );
  return { markdown: collapsed, images };
}

export function expandMarkdownDataImages(
  markdown: string,
  images: HiddenMarkdownImage[]
) {
  const queues = images.reduce<Map<string, string[]>>((result, image) => {
    const queue = result.get(image.token) || [];
    queue.push(image.fullMarkdown);
    result.set(image.token, queue);
    return result;
  }, new Map());
  return String(markdown || "").replace(
    /!\[[^\]\n]*\](?!\()/g,
    (token) => queues.get(token)?.shift() || token
  );
}

function countImagesBeforeCursor(
  markdown: string,
  images: HiddenMarkdownImage[]
) {
  let imageIndex = 0;
  const tokens = markdown.match(/!\[[^\]\n]*\](?!\()/g) || [];
  tokens.forEach((token) => {
    if (images[imageIndex]?.token === token) {
      imageIndex += 1;
    }
  });
  return imageIndex;
}

interface MarkdownRequirementEditorProps {
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onError?: (message: string) => void;
  onImageClick?: (src: string) => void;
  onSourceCollapsedChange?: (collapsed: boolean) => void;
  showPreview?: boolean;
  sourceCollapsed?: boolean;
  value: string;
}

function safeImageUri(src: string) {
  const normalized = String(src || "").trim();
  if (
    /^data:image\/(?:png|jpe?g|gif|webp);base64,/i.test(normalized) ||
    /^https?:\/\//i.test(normalized) ||
    /^(?:\/|\.\/|\.\.\/)/.test(normalized) ||
    !/^[a-z][a-z\d+.-]*:/i.test(normalized)
  ) {
    return normalized;
  }
  return "";
}

const MarkdownRequirementEditor: FC<MarkdownRequirementEditorProps> = ({
  ariaLabel = "项目需求 Markdown",
  className = "",
  disabled = false,
  onChange,
  onError,
  onImageClick,
  onSourceCollapsedChange,
  showPreview = true,
  sourceCollapsed = false,
  value,
}) => {
  const isPreviewOnly = showPreview && sourceCollapsed;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const scrollSyncOwnerRef = useRef<"source" | "preview" | null>(null);
  const scrollSyncFrameRef = useRef<number | undefined>();
  const valueRef = useRef(value);
  valueRef.current = value;
  const collapsedValue = collapseMarkdownDataImages(value);

  useEffect(
    () => () => {
      if (scrollSyncFrameRef.current !== undefined) {
        window.cancelAnimationFrame(scrollSyncFrameRef.current);
      }
    },
    []
  );

  function emitVisibleMarkdown(nextMarkdown: string) {
    const currentImages = collapseMarkdownDataImages(valueRef.current).images;
    const nextValue = expandMarkdownDataImages(nextMarkdown, currentImages);
    valueRef.current = nextValue;
    onChange(nextValue);
  }

  function insertPastedImage(alt: string, src: string) {
    const textarea = textareaRef.current;
    const currentCollapsed = collapseMarkdownDataImages(valueRef.current);
    const currentMarkdown = currentCollapsed.markdown;
    const selectionStart = textarea?.selectionStart ?? currentMarkdown.length;
    const selectionEnd = textarea?.selectionEnd ?? currentMarkdown.length;
    const prefix = currentMarkdown.slice(0, selectionStart);
    const suffix = currentMarkdown.slice(selectionEnd);
    const separatorBefore = prefix && !prefix.endsWith("\n") ? "\n" : "";
    const separatorAfter = suffix && !suffix.startsWith("\n") ? "\n" : "";
    const token = `![${alt}]`;
    const nextMarkdown = `${prefix}${separatorBefore}${token}${separatorAfter}${suffix}`;
    const imageIndex = countImagesBeforeCursor(prefix, currentCollapsed.images);
    const nextImages = [...currentCollapsed.images];
    nextImages.splice(imageIndex, 0, {
      token,
      fullMarkdown: `${token}(${src})`,
    });
    const nextValue = expandMarkdownDataImages(nextMarkdown, nextImages);
    if (nextValue.length > MAX_CONTENT_LENGTH) {
      onError?.("项目需求内容过大，请减少粘贴图片后重试");
      return;
    }
    valueRef.current = nextValue;
    onChange(nextValue);
  }

  function handlePaste(event: React.ClipboardEvent<HTMLTextAreaElement>) {
    if (disabled) {
      return;
    }
    const imageFiles = Array.from(event.clipboardData.files || []).filter(
      (file) => file.type.startsWith("image/")
    );
    if (!imageFiles.length) {
      return;
    }
    event.preventDefault();
    imageFiles.forEach((file) => {
      if (!SUPPORTED_PASTED_IMAGE_TYPES.has(file.type)) {
        onError?.(`图片 ${file.name || "未命名图片"} 格式不受支持`);
        return;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        onError?.(`图片 ${file.name || "未命名图片"} 超过 2MB，无法粘贴`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const alt = (file.name || "项目需求图片")
          .split("[")
          .join("-")
          .split("]")
          .join("-")
          .replace(/\r?\n/g, "-");
        insertPastedImage(alt, String(reader.result || ""));
      };
      reader.onerror = () => onError?.("图片读取失败，请重新粘贴");
      reader.readAsDataURL(file);
    });
  }

  function synchronizeScroll(
    source: HTMLElement,
    target: HTMLElement,
    owner: "source" | "preview"
  ) {
    if (
      scrollSyncOwnerRef.current &&
      scrollSyncOwnerRef.current !== owner
    ) {
      return;
    }
    scrollSyncOwnerRef.current = owner;
    const sourceRange = Math.max(0, source.scrollHeight - source.clientHeight);
    const targetRange = Math.max(0, target.scrollHeight - target.clientHeight);
    const progress = sourceRange
      ? Math.min(1, Math.max(0, source.scrollTop / sourceRange))
      : 0;
    target.scrollTop = progress * targetRange;
    if (scrollSyncFrameRef.current !== undefined) {
      window.cancelAnimationFrame(scrollSyncFrameRef.current);
    }
    scrollSyncFrameRef.current = window.requestAnimationFrame(() => {
      scrollSyncOwnerRef.current = null;
      scrollSyncFrameRef.current = undefined;
    });
  }

  return (
    <div
      className={`markdownRequirementEditor${
        showPreview ? "" : " markdownRequirementEditor--sourceOnly"
      }${
        isPreviewOnly ? " markdownRequirementEditor--previewOnly" : ""
      } ${className}`.trim()}
    >
      {!isPreviewOnly && (
        <section className="markdownRequirementEditor__pane markdownRequirementEditor__source">
          <header>
            <span><FormOutlined /> Markdown 编辑</span>
            <div className="markdownRequirementEditor__headerActions">
              <small>
                {disabled
                  ? "只读"
                  : `${collapsedValue.markdown.length.toLocaleString()} 字符${
                      collapsedValue.images.length
                        ? ` · ${collapsedValue.images.length} 张图片`
                        : ""
                    }`}
              </small>
              {showPreview && onSourceCollapsedChange && (
                <Tooltip title="收起编辑区，仅查看预览">
                  <Button
                    aria-label="收起 Markdown 编辑区"
                    className="markdownRequirementEditor__collapseButton"
                    icon={<DoubleLeftOutlined />}
                    onClick={() => onSourceCollapsedChange(true)}
                    size="small"
                    type="text"
                  />
                </Tooltip>
              )}
            </div>
          </header>
          <textarea
            aria-label={ariaLabel}
            disabled={disabled}
            onChange={(event) => emitVisibleMarkdown(event.target.value)}
            onPaste={handlePaste}
            onScroll={(event) => {
              if (previewRef.current) {
                synchronizeScroll(event.currentTarget, previewRef.current, "source");
              }
            }}
            placeholder="使用 Markdown 编写项目背景、目标、范围和验收要求……"
            ref={textareaRef}
            spellCheck={false}
            value={collapsedValue.markdown}
          />
          {!disabled && (
            <footer>
              <PictureOutlined />
              支持直接粘贴图片，单张不超过 2MB
            </footer>
          )}
        </section>
      )}

      {showPreview && (
        <section className="markdownRequirementEditor__pane markdownRequirementEditor__preview">
          <header>
            <span><EyeOutlined /> 实时预览</span>
            <div className="markdownRequirementEditor__headerActions">
              <small>{isPreviewOnly ? "纯享预览" : "Markdown"}</small>
              {isPreviewOnly && onSourceCollapsedChange && (
                <Button
                  aria-label="展开 Markdown 编辑区"
                  className="markdownRequirementEditor__expandButton"
                  icon={<DoubleRightOutlined />}
                  onClick={() => onSourceCollapsedChange(false)}
                  size="small"
                >
                  展开编辑
                </Button>
              )}
            </div>
          </header>
          <div
            className="markdownRequirementEditor__rendered"
            onScroll={(event) => {
              if (textareaRef.current) {
                synchronizeScroll(event.currentTarget, textareaRef.current, "preview");
              }
            }}
            ref={previewRef}
          >
            {value.trim() ? (
              <ReactMarkdown
                components={{
                  a: ({ node: _node, children, ...props }) => (
                    <a {...props} rel="noreferrer" target="_blank">
                      {children}
                    </a>
                  ),
                  img: ({ node: _node, ...props }) => (
                    <img
                      {...props}
                      alt={props.alt || "项目需求图片"}
                      onClick={() => props.src && onImageClick?.(props.src)}
                    />
                  ),
                }}
                remarkPlugins={[remarkGfm]}
                transformImageUri={safeImageUri}
              >
                {value}
              </ReactMarkdown>
            ) : (
              <div className="markdownRequirementEditor__empty">
                <EyeOutlined />
                <strong>预览区域</strong>
                <span>左侧输入后，这里会实时呈现最终效果。</span>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default MarkdownRequirementEditor;
